import merge from 'lodash/merge'

import type { Location, To as ReactRouterTo } from 'react-router-dom'
import type { ItemWithUpdatedFields } from 'types/nesting'
import type { Track, TrackId } from 'types/track'

type LinkLocation = { to: ReactRouterTo; state: any }

import { AUDIO_FILE } from '@disco/constants/FileTypes'
import {
  ALREADY_NESTED_TRACK,
  MAIN,
  NESTED_INTO_ITSELF,
  NESTED_TRACK_INTO_ANOTHER_TRACK,
  STEM,
} from '@disco/constants/Nesting'

// import type { ManageNestingFormState } from '@disco/state-management/reducers/forms/manage-nesting'
type ManageNestingFormState = any

import { getFileTypeByExt } from '@disco/utils/file-utils'
import { addItem, deleteItem, moveItems2 } from '@disco/utils/immutable'
import { getNestedTracksCount, getStemsCount } from '@disco/utils/tracks'

/**
 * Sorts tracks by their nesting position with special handling for MAIN tracks.
 *
 * Ensures MAIN tracks always appear first when positions are equal,
 * and handles cases where positions might be undefined.
 *
 * @param items - Array of tracks to sort
 * @returns Sorted array of tracks by nesting position
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { id: 1, nesting_position: 1, nesting_label: 'VERSION' },
 *   { id: 2, nesting_position: 0, nesting_label: 'MAIN' },
 * ]
 * const sorted = sortByPosition(tracks)
 * // MAIN track with position 1 will come before VERSION track with position 1
 * ```
 */
const sortByPosition = (items: Array<Track>) => {
  const sortedItems = [...items]
  sortedItems.sort((a, b) => {
    if (a.nesting_position === b.nesting_position) {
      // ExplorerTrackVersions doesn't render main track in Sortable
      // because of that we can get a case when we have not MAIN track on 0 position
      if (a.nesting_label === MAIN) {
        return -1
      } else {
        return 1
      }
    }
    return (a.nesting_position || 0) - (b.nesting_position || 0)
  })
  return sortedItems
}

/**
 * Extracts and sorts version tracks (non-STEM tracks) from a tracks array.
 *
 * Filters out STEM tracks and returns only version tracks sorted by position.
 *
 * @param tracks - Array of tracks to filter and sort
 * @returns Sorted array containing only version tracks
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { id: 1, nesting_label: 'MAIN' },
 *   { id: 2, nesting_label: 'STEM' },
 *   { id: 3, nesting_label: 'VERSION' }
 * ]
 * const versions = getVersionsFromTracksArray(tracks)
 * // Returns [{ id: 1, nesting_label: 'MAIN' }, { id: 3, nesting_label: 'VERSION' }]
 * ```
 */
export const getVersionsFromTracksArray = (tracks: Track[]): Track[] =>
  sortByPosition(tracks.filter(({ nesting_label }) => nesting_label !== STEM))

/**
 * Extracts and sorts STEM tracks from a tracks array.
 *
 * Filters to include only STEM tracks and returns them sorted by position.
 *
 * @param tracks - Array of tracks to filter and sort
 * @returns Sorted array containing only STEM tracks
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { id: 1, nesting_label: 'MAIN' },
 *   { id: 2, nesting_label: 'STEM' },
 *   { id: 3, nesting_label: 'STEM' }
 * ]
 * const stems = getStemsFromTracksArray(tracks)
 * // Returns [{ id: 2, nesting_label: 'STEM' }, { id: 3, nesting_label: 'STEM' }]
 * ```
 */
export const getStemsFromTracksArray = (tracks: Track[]): Track[] =>
  sortByPosition(tracks.filter(({ nesting_label }) => nesting_label === STEM))

/**
 * Generates updated nesting fields for tracks with corrected positions.
 *
 * Creates a mapping of track IDs to updated nesting fields, ensuring
 * positions are sequential starting from 0.
 *
 * @param tracks - Array of tracks to generate position updates for
 * @returns Object mapping track IDs to updated nesting fields
 *
 * @example
 * ```typescript
 * const tracks = [
 *   { id: 1, nesting_parent_id: 'parent1', nesting_label: 'MAIN' },
 *   { id: 2, nesting_parent_id: 'parent1', nesting_label: 'VERSION' }
 * ]
 * const updates = shiftPosition(tracks)
 * // {
 * //   1: { nesting_position: 0, nesting_parent_id: 'parent1', nesting_label: 'MAIN' },
 * //   2: { nesting_position: 1, nesting_parent_id: 'parent1', nesting_label: 'VERSION' }
 * // }
 * ```
 */
export const shiftPosition = (
  tracks: Track[],
): Partial<Record<TrackId, ItemWithUpdatedFields>> => {
  const itemsWithUpdatedFields: Record<string, any> = {}

  // tracks contains only version or only stems
  tracks.forEach((item, index) => {
    itemsWithUpdatedFields[item.id] = {
      nesting_position: index,
      nesting_parent_id: item.nesting_parent_id,
      nesting_label: item.nesting_label,
    }
  })

  return itemsWithUpdatedFields
}

/**
 * Unlinks a track from its nested group and adjusts positions of remaining tracks.
 *
 * Removes the specified track from its nesting group and shifts the positions
 * of remaining tracks to maintain sequential ordering.
 *
 * @param params - Object containing track and tracks array
 * @param params.track - The track to unlink from nesting
 * @param params.tracks - Array of all tracks in the nested group
 * @returns Form state with updated nesting fields for affected tracks
 *
 * @example
 * ```typescript
 * const track = { id: 2, nesting_label: 'VERSION', nesting_position: 1 }
 * const tracks = [
 *   { id: 1, nesting_label: 'MAIN', nesting_position: 0 },
 *   { id: 2, nesting_label: 'VERSION', nesting_position: 1 },
 *   { id: 3, nesting_label: 'VERSION', nesting_position: 2 }
 * ]
 * const result = unlinkTrackAndShiftPosition({ track, tracks })
 * // Unlinks track 2 and shifts track 3 to position 1
 * ```
 */
export const unlinkTrackAndShiftPosition = ({
  track,
  tracks,
}: {
  track: Track
  tracks: Track[]
}): Partial<ManageNestingFormState> => {
  let sortedItems: Array<Track> | Array<never> = []
  if (track.nesting_label === STEM) {
    sortedItems = getStemsFromTracksArray(tracks)
  } else {
    sortedItems = getVersionsFromTracksArray(tracks)
  }

  const index = sortedItems.findIndex(item => item?.id === track.id)
  let itemsWithUpdatedFields = {
    [track.id]: {
      nesting_parent_id: null,
      nesting_label: null,
      nesting_position: null,
    },
  }

  itemsWithUpdatedFields = merge(
    itemsWithUpdatedFields,
    shiftPosition(deleteItem(sortedItems, index)),
  )

  return { itemsWithUpdatedFields }
}

/**
 * Updates a track's nesting fields and shifts positions of related tracks.
 *
 * Updates the specified track with new nesting fields and adjusts positions
 * of other tracks in the same nesting group to maintain proper ordering.
 *
 * @param params - Object containing tracks, track to update, and shift options
 * @param params.tracks - Array of all tracks in the nesting group
 * @param params.track - The track to update
 * @param params.newFields - New nesting fields to apply to the track
 * @param params.sortedItems - Optional pre-sorted items array
 * @param params.shiftStems - Whether to shift STEM track positions
 * @param params.shiftVersions - Whether to shift VERSION track positions
 * @returns Object with updated nesting fields for affected tracks
 */
export const updateTrackAndShiftPosition = ({
  // tracks contains versions and stems
  tracks,

  track,
  newFields,

  // sortedItems contains versions or stems
  sortedItems,

  shiftStems,
  shiftVersions,
}: {
  tracks: Track[]
  track: Track
  newFields: ItemWithUpdatedFields
  sortedItems?: Track[]
  shiftStems: boolean
  shiftVersions: boolean
}): {
  itemsWithUpdatedFields: Partial<Record<TrackId, ItemWithUpdatedFields>>
} => {
  let newItemsWithUpdatedFields = {
    [track.id]: {
      ...newFields,
    },
  }

  // case: moving tracks between categories with dnd or without
  if (shiftStems || shiftVersions) {
    const versions = getVersionsFromTracksArray(tracks)
    const stems = getStemsFromTracksArray(tracks)

    let addToArray = shiftStems ? stems : versions
    let removeFromArray = shiftStems ? versions : stems
    const position = newFields.nesting_position || 0

    const trackIndex = addToArray.findIndex(t => t.id === track.id)
    addToArray =
      trackIndex === -1
        ? addItem(addToArray, position, {
            ...track,
            newFields,
          } as any)
        : moveItems2(addToArray, trackIndex, newFields.nesting_position || 0)
    newItemsWithUpdatedFields = {
      ...merge(shiftPosition(addToArray), newItemsWithUpdatedFields),
    }

    const index = removeFromArray.findIndex(({ id }) => id === track.id)
    if (index !== -1) {
      removeFromArray = deleteItem(removeFromArray, index)
      newItemsWithUpdatedFields = {
        ...merge(shiftPosition(removeFromArray), newItemsWithUpdatedFields),
      }
    }

    if (newFields.nesting_label === MAIN) {
      newItemsWithUpdatedFields = adjustItems(
        newItemsWithUpdatedFields,
        track.id,
      )
    }
    return { itemsWithUpdatedFields: newItemsWithUpdatedFields }
  }

  // cases:
  // 1. moving tracks inside categories with dnd
  // 2. unlink track
  if (sortedItems) {
    const index = sortedItems.findIndex(({ id }) => id === track.id)
    let updatedTracks = deleteItem(sortedItems, index)
    updatedTracks = addItem(updatedTracks, newFields.nesting_position || 0, {
      ...track,
      newFields,
    })
    newItemsWithUpdatedFields = merge(
      shiftPosition(updatedTracks),
      newItemsWithUpdatedFields,
    )
    if (newFields.nesting_position === 0) {
      newItemsWithUpdatedFields = adjustItems(
        newItemsWithUpdatedFields,
        track.id,
      )
    } else if (newFields.nesting_label === MAIN) {
      const newParent = Object.entries(newItemsWithUpdatedFields).find(
        ([, t]: [any, any]) =>
          (t as ItemWithUpdatedFields).nesting_position === 0,
      )

      if (newParent) {
        newItemsWithUpdatedFields = {
          ...newItemsWithUpdatedFields,
          [newParent[0]]: {
            ...newItemsWithUpdatedFields[newParent[0]],
            nesting_parent_id: null,
            nesting_label: MAIN,
          },
        }
        newItemsWithUpdatedFields = adjustItems(
          newItemsWithUpdatedFields,
          Number(newParent[0]),
        )
      }
    }
    return { itemsWithUpdatedFields: newItemsWithUpdatedFields }
  } else {
    // case: change track label
    return { itemsWithUpdatedFields: newItemsWithUpdatedFields }
  }
}

/**
 * Adds new tracks to a nesting group and shifts positions accordingly.
 *
 * Integrates new tracks into an existing nesting group and adjusts positions
 * of all tracks to maintain proper sequential ordering.
 *
 * @param params - Object containing existing tracks and new tracks to add
 * @param params.tracks - Array of existing tracks in the nesting group
 * @param params.newTracks - Array of new tracks to add to the group
 * @returns Object with updated nesting fields for all affected tracks
 *
 * @example
 * ```typescript
 * const existingTracks = [{ id: 1, nesting_label: 'MAIN' }]
 * const newTracks = [{ id: 2, nesting_label: 'VERSION' }]
 * const result = addNewTracksAndShiftPosition({ tracks: existingTracks, newTracks })
 * // Returns position updates for both existing and new tracks
 * ```
 */
export const addNewTracksAndShiftPosition = ({
  tracks,
  newTracks,
}: {
  tracks: Track[]
  newTracks: Track[]
}): {
  itemsWithUpdatedFields: Partial<Record<TrackId, ItemWithUpdatedFields>>
} => {
  // "Uploaded tracks should be added to the bottom of the nested versions section."
  // https://disco-team.atlassian.net/browse/DISCO-3229

  const versions = getVersionsFromTracksArray(tracks)
  const { itemsWithUpdatedFields } = newTracks.reduce(
    (acc, track, index) => {
      return {
        tracks: [...acc.tracks, track],
        itemsWithUpdatedFields: {
          ...acc.itemsWithUpdatedFields,
          [track.id]: {
            nesting_label: track?.nesting_label || null,
            nesting_parent_id: track.nesting_parent_id,
            nesting_position: acc.tracks.length + index,
          },
        },
      }
    },
    {
      tracks: versions,
      itemsWithUpdatedFields: {},
    },
  )
  return { itemsWithUpdatedFields }
}

const setMainTrackForNestedSet = (
  // nestedTracks contains versions and stems
  nestedTracks: Track[],
  newMainTrack: Track,
): {
  itemsWithUpdatedFields: Partial<Record<TrackId, ItemWithUpdatedFields>>
  newMainTrack: Track | null | undefined
} => {
  const itemsWithUpdatedFields: Record<string, any> = {}

  nestedTracks.forEach(track => {
    itemsWithUpdatedFields[track.id] = {
      nesting_parent_id: newMainTrack.id,
      nesting_label: track.nesting_label === MAIN ? null : track.nesting_label,
    }
  })
  itemsWithUpdatedFields[newMainTrack.id] = {
    nesting_parent_id: null,
    nesting_label: MAIN,
  }

  return {
    itemsWithUpdatedFields,
    newMainTrack,
  }
}

/**
 * Removes STEM tracks from a track's nested collection.
 *
 * Creates a new track object with all STEM tracks removed from its
 * nested tracks collection, keeping only versions.
 *
 * @param track - The track to remove stems from
 * @returns New track object without STEM tracks
 *
 * @example
 * ```typescript
 * const track = {
 *   id: 1,
 *   nested_tracks: [
 *     { id: 2, nesting_label: 'VERSION' },
 *     { id: 3, nesting_label: 'STEM' }
 *   ]
 * }
 * const trackWithoutStems = removeStemsFromTrack(track)
 * // Returns track with only VERSION nested tracks
 * ```
 */
export const removeStemsFromTrack = (track: Track): Track => {
  let result = track
  if (getStemsCount(result) > 0) {
    result = {
      ...result,
      nested_stem_count: 0,
      nested_tracks: track.nested_tracks?.filter(
        ({ nesting_label }) => nesting_label === STEM,
      ),
    }
  }
  if (getNestedTracksCount(result) === 0) {
    result = {
      ...result,
      nesting_label: null,
    }
  }
  return result
}

/**
 * Merges tracks with their updated nesting fields.
 *
 * Applies updated nesting fields to the corresponding tracks in the array,
 * creating new track objects with the merged data.
 *
 * @param tracks - Array of tracks to merge with updated fields
 * @param updatedFields - Object mapping track IDs to their updated nesting fields
 * @returns Array of tracks with updated nesting fields applied
 *
 * @example
 * ```typescript
 * const tracks = [{ id: 1, title: 'Track 1' }, { id: 2, title: 'Track 2' }]
 * const updatedFields = {
 *   1: { nesting_position: 0, nesting_label: 'MAIN' },
 *   2: { nesting_position: 1, nesting_label: 'VERSION' }
 * }
 * const mergedTracks = mergeTracksWithUpdatedFields(tracks, updatedFields)
 * // Returns tracks with nesting fields applied
 * ```
 */
export const mergeTracksWithUpdatedFields = (
  tracks: Track[],
  updatedFields: Partial<Record<TrackId, ItemWithUpdatedFields>>,
): Track[] => {
  const result = tracks
    .map((track: Track): Track | null => {
      const newFields = updatedFields?.[track.id]
      if (
        newFields &&
        newFields.nesting_parent_id === null &&
        newFields.nesting_label !== MAIN
      ) {
        return null
      }

      return { ...track, ...newFields }
    })
    .filter((track): track is Track => track !== null)

  result.sort((a: Track): number => {
    if (a.nesting_label === MAIN) {
      return -1
    }
    return 0
  })

  return result
}

/**
 * Creates default nesting state for a nested set of tracks.
 *
 * Generates initial nesting fields for a collection of tracks being
 * organized into a nested set, with proper positioning and labels.
 *
 * @param nestedTracks - Array of tracks to be nested (versions and stems)
 * @param mainTrack - The main track that will be the parent
 * @returns Object mapping track IDs to their default nesting fields
 *
 * @example
 * ```typescript
 * const nestedTracks = [
 *   { id: 2, title: 'Version 1' },
 *   { id: 3, title: 'Stem 1' }
 * ]
 * const mainTrack = { id: 1, title: 'Main Track' }
 * const defaultState = createDefaultStateForNestedSet(nestedTracks, mainTrack)
 * // Returns default nesting fields for all tracks
 * ```
 */
export const createDefaultStateForNestedSet = (
  // nestedTracks contains versions and stems
  nestedTracks: Track[],
  mainTrack: Track,
): Partial<Record<TrackId, ItemWithUpdatedFields>> => {
  const nestedSet = setMainTrackForNestedSet(nestedTracks, mainTrack)
  const result = mergeTracksWithUpdatedFields(
    nestedTracks,
    nestedSet.itemsWithUpdatedFields,
  )
  const stems = getStemsFromTracksArray(result)
  const versions = getVersionsFromTracksArray(result)

  return merge(shiftPosition(versions), shiftPosition(stems))
}

/**
 * Creates a location object for managing nested set operations.
 *
 * Generates a location object that can be used for navigation to the
 * nested set management interface for the given tracks.
 *
 * @param tracks - Array of tracks to create nested set location for
 * @returns Location object for nested set management or null if not applicable
 *
 * @example
 * ```typescript
 * const tracks = [{ id: 1, title: 'Track 1' }, { id: 2, title: 'Track 2' }]
 * const location = createNestedSetLocation(tracks)
 * // Returns location object for managing these tracks as a nested set
 * ```
 */
export const createNestedSetLocation = (
  tracks: Track[],
): LinkLocation | null => {
  const tracksIds = tracks
    .filter((track: Track): boolean => {
      const fileType = getFileTypeByExt(track.ext)

      return AUDIO_FILE === fileType
    })
    .map<TrackId>((track: Track): TrackId => track.id)

  const flatNestedTrackIds = tracksIds.flatMap(trackId => {
    const track = tracks.find(track => track.id === trackId)
    if (!track) return []

    const nestedTrackIds = track.nested_tracks?.map(({ id }) => id) || []
    return [track.id, ...nestedTrackIds]
  })

  const mainTrackId = tracksIds.shift()

  if (!mainTrackId) {
    return null
  }

  const mainTrack = tracks.find(track => track.id === mainTrackId)
  const nestedTracks = tracks.filter(track => tracksIds.includes(track.id))

  let validationModalLocations: LinkLocation[] = []
  const manageNestingLocation: LinkLocation = {
    to: {
      pathname: `/explorer/tracks/${mainTrackId}/manage-nesting`,
      search: '',
      hash: '',
    },
    state: {
      mainTrackId,
      tracksIds: flatNestedTrackIds,
    },
  }

  if (mainTrack) {
    validationModalLocations = validateNestedSet({
      mainTrack,
      nestedTracks,
      manageNestingLocation,
    })
    // sort errors: critical errors first, the rest without changing positions, e.g NESTED_INTO_ITSELF
    validationModalLocations.sort(location => {
      if (location.state?.openLocation) {
        return 1
      }
      return 0
    })

    // create linked list: error modal > error modal > manage nesting modal
    validationModalLocations.forEach((location, index) => {
      const nextLocation = validationModalLocations[index + 1]

      // critical errors don't have openLocation, e.g NESTED_INTO_ITSELF
      if (location.state?.openLocation && nextLocation) {
        // eslint-disable-next-line no-param-reassign
        location.state.openLocation = nextLocation
      }
    })
  }

  return validationModalLocations[0] || manageNestingLocation
}

// TODO: refactor nesting validation.
// Use one common function to validate nesting set
/**
 * Validates a nested set configuration and returns validation errors.
 *
 * Checks for various validation rules when creating or modifying nested sets,
 * such as preventing nesting into itself or invalid track combinations.
 *
 * @param params - Object containing validation parameters
 * @param params.mainTrack - The main track for the nested set
 * @param params.nestedTracks - Array of tracks to be nested
 * @param params.manageNestingLocation - Optional location for nesting management
 * @returns Array of location objects representing validation errors
 *
 * @example
 * ```typescript
 * const mainTrack = { id: 1, title: 'Main Track' }
 * const nestedTracks = [{ id: 1, title: 'Same Track' }] // Invalid: same as main
 * const errors = validateNestedSet({ mainTrack, nestedTracks })
 * // Returns validation errors for invalid nesting configuration
 * ```
 */
export const validateNestedSet = ({
  mainTrack,
  nestedTracks,
  manageNestingLocation,
}: {
  mainTrack: Track
  nestedTracks: Track[]
  manageNestingLocation?: LinkLocation
}): Array<LinkLocation> => {
  const errors: Array<LinkLocation> = []
  const trackIds = nestedTracks.map(t => t.id)

  if (trackIds.length === 0) return []

  if (trackIds.includes(mainTrack.id)) {
    errors.push({
      to: {
        pathname: `/explorer/tracks/${mainTrack.id}/nesting-validation`,
        search: '',
        hash: '',
      },
      state: { type: NESTED_INTO_ITSELF },
    })
  }

  const alreadyNestedTracks = nestedTracks.filter(
    track =>
      track.nesting_parent_id !== null &&
      track.nesting_parent_id !== mainTrack.id,
  )

  if (alreadyNestedTracks.length > 0) {
    alreadyNestedTracks.forEach(alreadyNestedTrack => {
      errors.push({
        to: {
          pathname: `/explorer/tracks/${mainTrack.id}/nesting-validation`,
          search: '',
          hash: '',
        },
        state: {
          nestedTracks,
          nestingLabel: alreadyNestedTrack.nesting_label,
          newVersionOf: mainTrack.id,
          track: alreadyNestedTrack.id,
          type: ALREADY_NESTED_TRACK,
          versionOf: alreadyNestedTrack.nesting_parent_id,
          openLocation: manageNestingLocation,
        },
      })
    })
  }

  // TODO: make validateNestedSet a common validation function,
  // uncomment this block + add support for partial validation (TOO_MANY_VERSIONS should be ignored in createNestedSetLocation)
  // const newAudioFiles = nestedTracks.filter(
  //   item => item.nesting_parent_id !== mainTrack.id,
  // )

  // const versionsCount = getVersionsFromTracksArray(newAudioFiles).length
  // const stemsCount = getStemsFromTracksArray(newAudioFiles).length

  // const versionsOverQuota =
  //   getVersionsCount(mainTrack) + versionsCount > VERSIONS_LIMIT
  // const stemsOverQuota = getStemsCount(mainTrack) + stemsCount > STEMS_LIMIT

  // if ((versionsOverQuota || stemsOverQuota) && Boolean(newAudioFiles.length)) {
  //   errors.push({
  //     pathname: `/explorer/tracks/${mainTrack.id}/nesting-validation`,
  //     state: { type: TOO_MANY_VERSIONS },
  //   })
  // }

  const tracksWithNestedTracks = nestedTracks.filter(getNestedTracksCount)

  if (tracksWithNestedTracks.length) {
    tracksWithNestedTracks.forEach(trackWithNestedTracks => {
      errors.push({
        to: {
          pathname: `/explorer/tracks/${mainTrack.id}/nesting-validation`,
          search: '',
          hash: '',
        },
        state: {
          type: NESTED_TRACK_INTO_ANOTHER_TRACK,
          track: trackWithNestedTracks.id,
          newVersionOf: mainTrack.id,
          nestedTracks: nestedTracks,
          nestingLabel: null,
          openLocation: manageNestingLocation,
        },
      })
    })
  }

  return errors
}
function adjustItems(
  newItemsWithUpdatedFields: Partial<Record<TrackId, ItemWithUpdatedFields>>,
  parentId: TrackId,
) {
  return Object.entries(newItemsWithUpdatedFields).reduce<Record<string, any>>(
    (acc, entry) => {
      const [key, t] = entry as [string, ItemWithUpdatedFields]
      return {
        ...acc,
        [key]:
          t.nesting_label === MAIN && t.nesting_position !== 0
            ? { ...t, nesting_label: null, nesting_parent_id: parentId }
            : {
                ...t,
                nesting_parent_id:
                  t.nesting_parent_id !== parentId && t.nesting_label !== MAIN
                    ? parentId
                    : null,
              },
      }
    },
    {},
  )
}

type ItemWithUpdatedFieldsEntries = [TrackId, ItemWithUpdatedFields]

export const getUpdatedFieldsEntries: (
  arg1: Partial<Record<TrackId, ItemWithUpdatedFields>>,
) => ItemWithUpdatedFieldsEntries[] = itemsWithUpdatedFields =>
  Object.entries(itemsWithUpdatedFields).map(([id, fields]: [any, any]) => [
    Number(id),
    fields as ItemWithUpdatedFields,
  ])

/**
 * Determines if a track should be designated as the main track.
 *
 * Checks if a track meets the criteria to be the main track in a nested set:
 * must be at position 0 and not be a STEM track.
 *
 * @param track - The track with updated fields to check
 * @returns True if the track should be the main track, false otherwise
 *
 * @example
 * ```typescript
 * const mainTrack = { nesting_position: 0, nesting_label: 'MAIN' }
 * const stemTrack = { nesting_position: 0, nesting_label: 'STEM' }
 * const versionTrack = { nesting_position: 1, nesting_label: 'VERSION' }
 *
 * shouldBeMain(mainTrack) // true
 * shouldBeMain(stemTrack) // false - STEMs can't be main
 * shouldBeMain(versionTrack) // false - not at position 0
 * ```
 */
export const shouldBeMain = (track: ItemWithUpdatedFields): boolean =>
  track.nesting_position === 0 && track.nesting_label !== STEM
